import React from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import PluginConnections from './PluginConnections';
import { InfoCard, SparkAccordion } from 'sharedComponents/UI';
import SparkInfiniteScroll from 'sharedComponents/UI/SparkInfiniteScroll';
import SparkMainAppSearchFilters from 'components/common/SparkMainAppSearchFilters';
import HistoryCardSkeleton from 'sharedComponents/Skeleton/HistoryCardSkeleton';
import { WorkSpaceIcon } from 'components/SVG';
import { SortAscIcon, SortDescIcon } from 'components/SVG';
import { useInfinitePlugins } from 'hooks/Plugins/useInfinitePlugins';
import { useSparkSearchContext } from 'hooks/common/useSparkSearchContext';
import ConnectionListSkeleton from 'sharedComponents/Skeleton/HistorySkeleton/ConnectionListSkeleton';

type sortFields = 'name' | 'connections';

type sortOptions = {
  sortOrder: 'asc' | 'desc';
  sortField: sortFields;
};

const SORTABLE_FIELDS = {
  NAME: 'name',
  CONNECTIONS: 'connections',
} as const;

function ConnectionTableAccordion() {
  const [isAccordionOpen, setIsAccordionOpen] = React.useState<null | string>(
    null
  );

  const [sortOptions, setSortOptions] = React.useState<sortOptions>({
    sortOrder: 'asc',
    sortField: SORTABLE_FIELDS.NAME,
  });

  const { filter } = useSparkSearchContext();
  const { search: query } = filter;

  const {
    plugins,
    hasNextPage,
    fetchNextPage,
    isLoading: isPluginsLoading,
    isFetchingNextPage,
  } = useInfinitePlugins(query);

  const handleLatElementView = React.useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleAccordionToggle = (pluginID: string) => {
    setIsAccordionOpen((prev) => (prev === pluginID ? null : pluginID));
  };

  const sortedPlugins = React.useMemo(() => {
    if (!plugins) return null;

    const { sortField, sortOrder } = sortOptions;

    return [...plugins].sort((a, b) => {
      const aValue =
        sortField === SORTABLE_FIELDS.NAME
          ? a.plugin.attributes.name
          : a.pluginConnections;
      const bValue =
        sortField === SORTABLE_FIELDS.NAME
          ? b.plugin.attributes.name
          : b.pluginConnections;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [plugins, sortOptions]);

  const handleSortPlugins = (fieldName: sortFields) => {
    setSortOptions((prevSortOrder) => {
      return {
        sortOrder: prevSortOrder.sortOrder === 'asc' ? 'desc' : 'asc',
        sortField: fieldName,
      };
    });
  };

  const IconButtonElement = ({ fieldName }: { fieldName: sortFields }) => {
    return (
      <IconButton
        sx={{ padding: '5px' }}
        onClick={() => handleSortPlugins(fieldName)}
      >
        {sortOptions.sortField === fieldName &&
        sortOptions.sortOrder === 'asc' ? (
          <SortAscIcon width='12px' height='12px' />
        ) : (
          <SortDescIcon width='12px' height='12px' />
        )}
      </IconButton>
    );
  };

  return (
    <Box width='100%'>
      <SparkMainAppSearchFilters hideCategories={true} />
      {isPluginsLoading ? (
        <Box mt={3}>
          <ConnectionListSkeleton count={5} />
        </Box>
      ) : (
        <>
          <Box mt={2}>
            {query.length ? (
              <Typography
                fontFamily='Mont-Semibold'
                variant='h6'
                marginLeft={0.5}
              >{`${sortedPlugins?.length} ${
                sortedPlugins?.length === 1 ? 'result' : 'results'
              } for ${query}`}</Typography>
            ) : (
              <></>
            )}
          </Box>
          {!!sortedPlugins?.length ? (
            <>
              <Box display={'flex'} mb={1}>
                <Box display={'flex'} alignItems={'flex-start'} flex={0.68}>
                  <Typography
                    ml={9}
                    onClick={() => handleSortPlugins(SORTABLE_FIELDS.NAME)}
                    sx={{
                      cursor: 'pointer',
                      fontFamily: 'Mont-Semibold',
                      fontSize: '16px',
                      color: '#404040',
                    }}
                  >
                    Name
                  </Typography>
                  {sortOptions.sortField === 'name' && (
                    <IconButtonElement fieldName={SORTABLE_FIELDS.NAME} />
                  )}
                </Box>
                <Box display={'flex'} flex={1}>
                  <Typography
                    onClick={() =>
                      handleSortPlugins(SORTABLE_FIELDS.CONNECTIONS)
                    }
                    sx={{
                      cursor: 'pointer',
                      fontFamily: 'Mont-Semibold',
                      fontSize: '16px',
                      color: '#404040',
                    }}
                  >
                    Connections
                  </Typography>
                  {sortOptions.sortField === 'connections' && (
                    <IconButtonElement
                      fieldName={SORTABLE_FIELDS.CONNECTIONS}
                    />
                  )}
                </Box>
              </Box>
              <SparkInfiniteScroll
                onLastElementView={handleLatElementView}
                isLoading={isPluginsLoading}
                isFetchingNextPage={isFetchingNextPage}
                skeletonCount={5}
                childContainerProps={{ container: true }}
              >
                {sortedPlugins?.map(({ plugin, pluginConnections }) => {
                  const pluginID = plugin.id;
                  const { name, logoURL } = plugin.attributes;
                  return (
                    <Grid
                      container
                      item
                      xs={12}
                      alignItems={'center'}
                      key={pluginID}
                    >
                      <SparkAccordion
                        onAccordionToggle={() =>
                          handleAccordionToggle(pluginID)
                        }
                        titleContainerProps={{ flex: 1 }}
                        title={
                          <Grid container>
                            <Grid
                              item
                              xs={5}
                              display={'flex'}
                              alignItems={'center'}
                            >
                              <Box
                                ml={1}
                                alignItems={'center'}
                                display={'flex'}
                              >
                                {logoURL ? (
                                  <Box
                                    src={logoURL}
                                    alt={name}
                                    component='img'
                                    sx={{
                                      height: '20px',
                                      width: '20px',
                                      borderRadius: '20px',
                                    }}
                                  />
                                ) : (
                                  <WorkSpaceIcon height='40px' width='40px' />
                                )}
                              </Box>
                              <Typography ml={2}>{name}</Typography>
                            </Grid>
                            <Grid
                              item
                              xs={7}
                              display={'flex'}
                              alignItems={'center'}
                            >
                              {pluginConnections}
                            </Grid>
                          </Grid>
                        }
                        sx={{
                          backgroundColor: '#fff',
                          borderColor: '#EEEEEE',
                          borderRadius: '10px',
                          marginBottom: '10px',
                          cursor: 'pointer',
                        }}
                      >
                        <Box
                          gap={2}
                          sx={{ pl: 3, pr: 5 }}
                          display={'flex'}
                          flexDirection={'column'}
                          justifyContent={'space-around'}
                        >
                          <PluginConnections
                            plugin={plugin}
                            isOpen={isAccordionOpen === pluginID}
                          />
                        </Box>
                      </SparkAccordion>
                    </Grid>
                  );
                })}
              </SparkInfiniteScroll>
            </>
          ) : (
            <Box mt={1}>
              <InfoCard info={'No Connections found'} type='error' />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default ConnectionTableAccordion;
